import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { campaignService } from '../services/api';
import './CustomerProfile.css';

function CustomerProfile() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    phone_number: '',
    full_name: '',
    email: '',
    id_type: 'NRIC',
    id_number: '',
    country: 'MYS',
    address: '',
    city: '',
    state: '',
    postal_code: '',
  });

  // Load from localStorage only — no server search
  useEffect(() => {
    const saved = localStorage.getItem('spe_customer');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setFormData(prev => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(data).filter(([_, v]) => v !== null && v !== undefined)
          ),
        }));
      } catch {}
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.phone_number) {
      setError('No. telefon diperlukan');
      return;
    }
    if (!formData.full_name) {
      setError('Nama penuh diperlukan');
      return;
    }

    try {
      setSaving(true);
      await campaignService.updateCustomerProfile(formData);

      // Update localStorage
      localStorage.setItem('spe_customer', JSON.stringify(formData));

      setSuccess('Maklumat anda berjaya dikemaskini.');
      navigate('/'); 
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal mengemaskini maklumat');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    // Clear all spe_ localStorage entries
    localStorage.removeItem('spe_customer');
    localStorage.removeItem('spe_registered_campaigns');
    const keys = Object.keys(localStorage).filter(k => k.startsWith('spe_voucher_'));
    keys.forEach(k => localStorage.removeItem(k));

    setShowResetConfirm(false);
    setError(null);
    setSuccess('Data peranti berjaya diset semula.');
    setFormData({
      phone_number: '', full_name: '', email: '',
      id_type: 'NRIC', id_number: '', country: 'MYS',
      address: '', city: '', state: '', postal_code: '',
    });
  };

  const hasData = formData.phone_number && formData.full_name;

  return (
    <div className="profile-page animate-fade-in-up">
      <div className="profile-card">
        <h1>Maklumat Pelanggan</h1>
        <p className="profile-desc">
          {hasData
            ? 'Kemaskini maklumat peribadi anda di bawah.'
            : 'Sila masukkan maklumat anda. Data ini akan disimpan secara selamat.'}
        </p>

        <div className="encrypted-badge">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          Penyulitan AES-256 aktif
        </div>

        {success && <div className="success-message">{success}</div>}
        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSave}>
          <div className="form-section">
            <label>No. Telefon <span className="required">*</span></label>
            <input
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleChange}
              placeholder="cth. 60123456789"
              required
            />
          </div>

          <div className="form-section">
            <label>Nama Penuh <span className="required">*</span></label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              placeholder="Nama penuh anda"
              required
            />
          </div>

          <div className="form-section">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email || ''}
              onChange={handleChange}
              placeholder="cth. nama@email.com"
            />
          </div>

          <div className="form-section">
            <label>Jenis Pengenalan</label>
            <select name="id_type" value={formData.id_type || 'NRIC'} onChange={handleChange}>
              <option value="NRIC">MyKad (NRIC)</option>
              <option value="PASSPORT">Pasport</option>
              <option value="ARMY">No. Tentera</option>
            </select>
          </div>

          <div className="form-section">
            <label>
              {formData.id_type === 'NRIC' && 'No. Kad Pengenalan'}
              {formData.id_type === 'BRN' && 'No. Pendaftaran Perniagaan'}
              {formData.id_type === 'PASSPORT' && 'No. Pasport'}
              {formData.id_type === 'ARMY' && 'No. Tentera'}
            </label>
            <input
              type="text"
              name="id_number"
              value={formData.id_number || ''}
              onChange={handleChange}
              placeholder={
                formData.id_type === 'NRIC' ? 'cth. 901234567890' :
                formData.id_type === 'BRN' ? 'cth. 202301012345' :
                formData.id_type === 'PASSPORT' ? 'cth. A12345678' :
                'cth. 123456'
              }
            />
          </div>

          <div className="form-section">
            <label>Alamat</label>
            <input
              type="text"
              name="address"
              value={formData.address || ''}
              onChange={handleChange}
              placeholder="Alamat penuh"
            />
          </div>

          <div className="form-row">
            <div className="form-section">
              <label>Bandar</label>
              <input
                type="text"
                name="city"
                value={formData.city || ''}
                onChange={handleChange}
              />
            </div>
            <div className="form-section">
              <label>Negeri</label>
              <input
                type="text"
                name="state"
                value={formData.state || ''}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-section">
              <label>Poskod</label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code || ''}
                onChange={handleChange}
                placeholder="cth. 50000"
              />
            </div>
            <div className="form-section">
              <label>Negara</label>
              <select name="country" value={formData.country || 'MYS'} onChange={handleChange}>
                <option value="MYS">Malaysia</option>
                <option value="OTH">Lain-lain</option>
              </select>
            </div>
          </div>

          <button type="submit" disabled={saving} className="btn-submit">
            {saving ? 'Menyimpan...' : 'Simpan Maklumat'}
          </button>
        </form>

        {hasData && (
          <div className="reset-section">
            <div className="reset-divider" />
            <button
              type="button"
              className="btn-reset"
              onClick={() => setShowResetConfirm(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              Set Semula Akaun
            </button>
            <p className="reset-hint">Hapus semua data dan voucher yang telah didaftarkan</p>
          </div>
        )}
      </div>

      {showResetConfirm && createPortal(
        <div className="reset-overlay" onClick={() => setShowResetConfirm(false)}>
          <div className="reset-modal" onClick={e => e.stopPropagation()}>
            <div className="reset-modal-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
            <h3>Set Semula Akaun?</h3>
            <p>Tindakan ini akan menghapuskan semua voucher anda dan tidak boleh dibatalkan.</p>
            <div className="reset-modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowResetConfirm(false)}
              >
                Batal
              </button>
              <button
                className="btn-confirm-reset"
                onClick={handleReset}
              >
                Ya, Set Semula
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default CustomerProfile;
